import { type FlagColor } from '../types';

type FlagColorTokens = {
	/** Icon / swatch colour, straight from the design system palette. */
	color: string;
	/** Badge foreground, shaded from `color` to a relative luminance of ~0.021. */
	badgeText: string;
	/** Badge background, tinted from `color` to a relative luminance of ~0.752. */
	badgeBackground: string;
};

/**
 * Flag colours as configured by the user in Settings → Flags.
 *
 * `color` mirrors the `$labelColors` design system palette in
 * `apps/legacy/src/sass/_variables.scss`, which is what legacy renders for the swatch a user
 * actually picks. Keep the two in sync — `get-flag-color.spec.ts` fails if they drift, which is
 * how CHQ-7961 happened (DarkRed rendered as purple `#8D3A95` here long after the design system
 * moved it to red `#D3002F`).
 *
 * The badge tokens are derived from `color` using the transform the palette already encoded:
 * multiplicative RGB shading for the text, mix-to-white for the background. That keeps the badge
 * in the same hue as the icon and the settings swatch, at ~11:1 contrast.
 */
export const FLAG_COLORS: Record<FlagColor, FlagColorTokens> = {
	DARK_ORCHID: { color: '#D814AD', badgeText: '#4D073E', badgeBackground: '#F8D7F1' },
	DARK_RED: { color: '#D3002F', badgeText: '#580013', badgeBackground: '#F8D9E0' },
	DEEP_SKY_BLUE: { color: '#758CFF', badgeText: '#202645', badgeBackground: '#D9E0FF' },
	FOREST_GREEN: { color: '#006849', badgeText: '#002E21', badgeBackground: '#D3E5E0' },
	GAINSBORO: { color: '#CDD5FF', badgeText: '#26272F', badgeBackground: '#DAE0FF' },
	GOLD: { color: '#FFC244', badgeText: '#32260D', badgeBackground: '#FFDD97' },
	GREEN_YELLOW: { color: '#42D396', badgeText: '#0E2D20', badgeBackground: '#B3EDD5' },
	HOT_PINK: { color: '#FF6A8B', badgeText: '#421C24', badgeBackground: '#FFD7E0' },
	INDIGO: { color: '#080E8A', badgeText: '#080E87', badgeBackground: '#DFE0F0' },
	LIGHT_SALMON: { color: '#FFC2CF', badgeText: '#302527', badgeBackground: '#FFD7E0' },
	LIME_GREEN: { color: '#16B874', badgeText: '#062E1D', badgeBackground: '#BDEBD8' },
	ORANGE: { color: '#FF9736', badgeText: '#3A220C', badgeBackground: '#FFDBB9' },
	ORANGE_RED: { color: '#FF462D', badgeText: '#4C150D', badgeBackground: '#FFD8D3' },
	PERU: { color: '#D2A47A', badgeText: '#30261C', badgeBackground: '#EFDECF' },
	ROYAL_BLUE: { color: '#4C69F6', badgeText: '#1A2454', badgeBackground: '#DAE0FD' },
	SLATE_GRAY: { color: '#9BA9BF', badgeText: '#25282D', badgeBackground: '#DCE1E9' },
	TEAL: { color: '#0EB4BE', badgeText: '#032D2F', badgeBackground: '#BAEAEC' },
};

/** Used when a flag has no colour, or a colour this client does not know about. */
const FALLBACK: FlagColorTokens = { color: '#64748B', badgeText: '#1F2937', badgeBackground: '#F3F4F6' };

/** `DARK_RED` and `DarkRed` both key on `DARKRED`; the compact forms are all unambiguous. */
const compact = (color: string) => color.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

const BY_COMPACT_KEY = new Map(
	(Object.keys(FLAG_COLORS) as FlagColor[]).map((color) => [compact(color), color] as const)
);

/**
 * Flag colours reach us in several shapes: `DARK_RED` from GraphQL, `DarkRed` from the Python
 * API, and `DARKRED` from callers that uppercase before handing the value over. Resolve all of
 * them to the GraphQL enum key.
 */
export const normalizeFlagColor = (color?: FlagColor | string): FlagColor | undefined =>
	color ? BY_COMPACT_KEY.get(compact(color)) : undefined;

const tokensFor = (color?: FlagColor | string): FlagColorTokens => {
	const key = normalizeFlagColor(color);

	return key ? FLAG_COLORS[key] : FALLBACK;
};

export const getFlagColor = (color?: FlagColor | string): string => tokensFor(color).color;

export const getFlagBadgeColors = (color?: FlagColor | string): { text: string; background: string } => {
	const { badgeText, badgeBackground } = tokensFor(color);

	return { text: badgeText, background: badgeBackground };
};
